<template>
  <div class="azcon-calendar-card-skeleton" :class="{ 'azcon-calendar-card-skeleton--right': eventsPosition === 'right' }">
    <div class="azcon-calendar-card-skeleton__body">
      <!-- Surface -->
      <div class="azcon-calendar-card-skeleton__surface">
        <div class="azcon-calendar-card-skeleton__header">
          <div class="azcon-calendar-card-skeleton__nav"></div>
          <div class="azcon-calendar-card-skeleton__month"></div>
          <div class="azcon-calendar-card-skeleton__nav"></div>
        </div>
        <div class="azcon-calendar-card-skeleton__weekdays">
          <span v-for="i in 7" :key="'wd_' + i" class="azcon-calendar-card-skeleton__weekday"></span>
        </div>
        <div class="azcon-calendar-card-skeleton__grid">
          <div v-for="i in 35" :key="'cell_' + i" class="azcon-calendar-card-skeleton__cell">
            <span class="azcon-calendar-card-skeleton__day"></span>
          </div>
        </div>
        <div class="azcon-calendar-card-skeleton__summary">
          <div class="azcon-calendar-card-skeleton__summary-dot"></div>
          <div class="azcon-calendar-card-skeleton__summary-text"></div>
        </div>
      </div>

      <!-- Events -->
      <div class="azcon-calendar-card-skeleton__events">
      <div class="azcon-calendar-card-skeleton__events-header">
        <div class="azcon-calendar-card-skeleton__events-date"></div>
        <div class="azcon-calendar-card-skeleton__events-badge"></div>
      </div>
      <div v-for="i in 2" :key="'ev_' + i" class="azcon-calendar-card-skeleton__event">
        <div class="azcon-calendar-card-skeleton__event-indicator"></div>
        <div class="azcon-calendar-card-skeleton__event-body">
          <div class="azcon-calendar-card-skeleton__event-title"></div>
          <div class="azcon-calendar-card-skeleton__event-time"></div>
        </div>
      </div>
    </div>
    </div>

    <!-- Actions -->
    <div class="azcon-calendar-card-skeleton__actions">
      <div class="azcon-calendar-card-skeleton__link"></div>
      <div class="azcon-calendar-card-skeleton__btn"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AzconCalendarCardSkeleton",
  props: {
    eventsPosition: {
      type: String,
      default: "bottom",
      validator: (v) => ["bottom", "right"].includes(v),
    },
  },
};
</script>

<style lang="scss" scoped>
$shimmer: linear-gradient(90deg, #e8edf3 25%, #dce3eb 50%, #e8edf3 75%);

@mixin shimmer {
  background: $shimmer;
  background-size: 200% 100%;
  animation: azcon-shimmer 1.5s infinite;
}

@keyframes azcon-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.azcon-calendar-card-skeleton {
  &--right {
    .azcon-calendar-card-skeleton__body {
      display: grid;
      grid-template-columns: minmax(0, 50%) minmax(0, 50%);
      gap: 14px;
      align-items: stretch;
    }

    .azcon-calendar-card-skeleton__events {
      margin-top: 0;
    }

    @media (max-width: 960px) {
      .azcon-calendar-card-skeleton__body {
        grid-template-columns: 1fr;
      }

      .azcon-calendar-card-skeleton__events {
        margin-top: 14px;
      }
    }
  }

  &__surface {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #e2e8f0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__nav { width: 34px; height: 34px; border-radius: 10px; @include shimmer; }
  &__month { width: 120px; height: 18px; border-radius: 6px; @include shimmer; }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 4px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
  }

  &__weekday { display: block; margin: 0 auto; width: 18px; height: 10px; border-radius: 3px; @include shimmer; }

  &__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    padding: 4px 0;
  }

  &__cell {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    border-radius: 10px;
  }

  &__day { width: 20px; height: 13px; border-radius: 4px; @include shimmer; }

  &__summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 10px;
    margin-top: 6px;
    border-top: 1px solid #f1f5f9;
  }

  &__summary-dot { width: 8px; height: 8px; border-radius: 50%; @include shimmer; }
  &__summary-text { width: 80px; height: 12px; border-radius: 4px; @include shimmer; }

  &__events {
    margin-top: 14px;
    padding: 14px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
  }

  &__events-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__events-date { width: 110px; height: 14px; border-radius: 4px; @include shimmer; }
  &__events-badge { width: 36px; height: 22px; border-radius: 8px; @include shimmer; }

  &__event {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    margin-top: 8px;
  }

  &__event-indicator { width: 4px; height: 28px; border-radius: 2px; flex-shrink: 0; @include shimmer; }

  &__event-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__event-title { width: 75%; height: 13px; border-radius: 4px; @include shimmer; }
  &__event-time { width: 50%; height: 11px; border-radius: 4px; @include shimmer; }

  &__actions {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__link { width: 160px; height: 13px; border-radius: 4px; @include shimmer; }
  &__btn { width: 110px; height: 36px; border-radius: 10px; @include shimmer; }
}
</style>
